<script lang="ts">
import { defineComponent as DO_defineComponent } from 'vue';
export default /*#__PURE__*/ DO_defineComponent({
	name: 'ToDatePicker',
	componentName: 'ToDatePicker'
});
</script>
<template>
	<to-input :id="id" :key="langKey" ref="inputBox" class="to-date-picker" :flex="flex ? flex : null" :model-value="datedisplay" :required="required" :desc="desc" :color="color" :prefix="prefix" :suffix="suffix" :mode="mode" :prop-error-type="errorType" :prop-error-tip="errorTip" :placeholder="placeholder" :disabled="disabled" :sp-chars="spChars" :link="!inputable && !disabled" :clearable="clearable && !disabled" :width="width" :comparable="comparable" @focus="inputFocus" @blur="inputBlur" @click="handleClick" @clear="clear()" @input="handleDisplayChange">
		<template #suffix>
			<to-icon v-if="type === 'time'" class="to-date-picker-icon" :link="inputable" :class="disabled ? 'to-dis' : ''" value="time" @click.stop="showLayer"></to-icon>
			<to-icon v-else class="to-date-picker-icon" :link="inputable" :class="disabled ? 'to-dis' : ''" value="date" @click.stop="showLayer"></to-icon>
		</template>
		<Teleport to="body">
			<div class="to-date-picker" :class="setClass">
				<to-float ref="layerRef" class="to-date-picker-layer" :global="false" :lazy="lazy" :slide="layerSlide" :position="layerPosition" :padding="0.5" :width="isPc ? (type === 'datetime' ? 43 : 23) : ''" :align="optionAlign ? optionAlign : isPc ? 'right' : ''" @closed="handleLayerClose">
					<!-- PC面板 -->
					<to-scroll v-if="isPc || showCalendar">
						<to-calendar ref="calendar" v-model="date" class="to-date-picker-calendar" :time="time" :type="type" :multiple="multiple" :min="min" :max="max" :min-year="minYear" :max-year="maxYear" :disabled-date="disabledDate" :before-change="beforeChange" @change="select" @close="layerRef?.close()"> </to-calendar>
					</to-scroll>
					<!-- 移动端面板 -->
					<Picker v-else v-model="date" :time="time" :type="type" :multiple="multiple" :min="min" :max="max" @cancel="layerRef?.close()" @change="select" @clear="clear()"></Picker>
					<div v-if="showCloseLayer">
						<to-button @click="closeLayer">{{ t('to.datepicker.confirm') }}</to-button>
					</div>
				</to-float>
			</div>
		</Teleport>
	</to-input>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, nextTick, computed } from 'vue'
import { ToInput, ToIcon, ToFloat, ToScroll, ToCalendar } from '@touchvue/components'
import { t, langKey } from '@touchvue/locale'

import Picker from './Picker.vue'
import { useDatePicker } from './use-date-picker'

import type { PropType } from 'vue'
import { useDevice } from '@touchvue/hooks'

import { getDefaultFormat, type DatePickerType, type DatePickerValue } from './date-picker'

// 定义组件名称


// 定义props
const props = defineProps({
	flex: Boolean,
	suffix: {
		type: String,
		default: ''
	},
	prefix: {
		type: String,
		default: ''
	},
	showCalendar: {
		type: Boolean,
		default: false
	},
	lazy: {
		type: Boolean,
		default: true
	},
	desc: {
		type: String,
		default: ''
	},
	spChars: {
		type: [String, Boolean],
		default: '/,\\,[,],{,},"'
	},
	id: {
		type: String,
		default: ''
	},
	required: {
		type: Boolean,
		default: null
	},
	modelValue: {
		type: [String, Date] as PropType<DatePickerValue>,
		default: ''
	},
	value: {
		type: [String, Date],
		default: ''
	},
	placeholder: {
		type: String,
		default: ''
	},
	disabled: Boolean,
	inputable: {
		type: Boolean,
		default: true
	},
	type: {
		type: String as PropType<DatePickerType>,
		default: 'date'
	},
	multiple: {
		type: Boolean,
		default: false
	},
	minYear: {
		type: Number,
		default: 1900
	},
	maxYear: {
		type: Number,
		default: 2200
	},
	color: {
		type: String,
		default: 'default'
	},
	mode: {
		type: String,
		default: ''
	},
	width: {
		type: [Number, String],
		default: ''
	},
	valueFormat: {
		type: String,
		default(rawProps: { type: DatePickerType }) {
			return getDefaultFormat(rawProps.type)
		}
	},
	displayFormat: {
		type: String,
		default(rawProps: { type: DatePickerType }) {
			return getDefaultFormat(rawProps.type)
		}
	},
	time: {
		type: String,
		default: ''
	},
	comparable: Boolean,
	beforeChange: {
		type: Function as PropType<(value: string) => boolean | Promise<boolean>>,
		default: null
	},
	optionAlign: {
		type: String,
		default: ''
	},
	min: {
		type: [String, Date, Number],
		default: ''
	},
	max: {
		type: [String, Date, Number],
		default: ''
	},
	/**
	 * 禁用日期回调，仅支持PC模式（弹出日历选择）
	 */
	disabledDate: {
		type: Function as PropType<(date: Date) => boolean>,
		default: null
	},
	clearable: {
		type: Boolean,
		default: true
	}
})

// 定义emits
const emit = defineEmits(['update:modelValue', 'change', 'focus', 'blur', 'beforeChange'])

const {
	date,
	datedisplay,
	changeValue,
	cleanValue, // 更新为 cleanValue
	handleDisplayChange,
	handleDisplayBlur
} = useDatePicker(props, emit)

// 响应式数据
const errorType = ref('rule')
const errorTip = ref('')

const layerSlide = ref(false)
const layerPosition = ref('')
const disabled = ref(props.disabled)

// refs
const inputBox = ref(null)
const layerRef = ref<any>(null)
const calendar = ref<any>(null)

// 计算属性
const { isPc } = useDevice()

// 监听器
watch(
	() => props.disabled,
	val => {
		disabled.value = val
	}
)

// 生命周期
onMounted(() => {
	if (typeof window !== 'undefined' && window.innerWidth <= 768) {
		// isPc.value = false
		layerSlide.value = true
		layerPosition.value = 'bottom'
	}
})

const setClass = computed(() => {
	const arr = ['to-disable-target']
	if (errorTip.value) {
		arr.push('to-input-mode-line')
		arr.push('to-input-color-danger')
		arr.push('to-input-tip-show')
	} else {
		if (props.mode) {
			for (let i = 0; i < props.mode.split(' ').length; i++) {
				arr.push(`mode-${props.mode.split(' ')[i]}`)
			}
		}
	}
	return arr
})

const showCloseLayer = computed(() => {
	return (isPc.value || props.showCalendar) && props.multiple && (props.type === 'month' || props.type === 'date')
})

function handleLayerClose() {
	if (calendar.value) {
		calendar.value.timeLayer = false
		calendar.value.monthLayer = false
	}
}

function closeLayer() {
	layerRef.value?.close()
}

function select(value: string) {
	changeValue(value)
	if (showCloseLayer.value) return
	layerRef.value?.close()
}

function inputFocus() {
	errorTip.value = ''
	// showLayer()
	emit('focus', datedisplay.value)
}

function inputBlur() {
	if (props.inputable) {
		handleDisplayBlur()
	}
	emit('blur', datedisplay.value)
}

function clear() {
	cleanValue()
	if (typeof window !== 'undefined' && window.innerWidth > 768) {
		layerRef.value?.close()
	}
}

function handleClick() {
	if (!props.inputable) {
		showLayer()
	}
}

function showLayer() {
	// 表单校验的提示清除
	errorTip.value = inputBox.value.errorTip = ''
	if (!disabled.value) {
		layerRef.value?.toggle(inputBox.value.$el)
		nextTick(() => {
			if (calendar.value) {
				// calendar.value.initCalendar()
				calendar.value.setData()
			}
		})
	}
}

function setDisabled(f: boolean) {
	disabled.value = f
}

// 暴露方法
defineExpose({
	setDisabled
})
</script>

<!--<style>-->
<!--.to-date-picker.to-dis > .to-icon-date {-->
<!--    pointer-events: none;-->
<!--}-->
<!--</style>-->
